/**
 * Trunnion AI - Execution Controls
 * Pause/resume/cancel controls for active executions.
 */

import { Pause, Play, XCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { ExecutionStatus } from '@/types';

interface ExecutionControlsProps {
  executionId: string;
  status: ExecutionStatus;
  onPause?: () => void;
  onResume?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  loading?: boolean;
}

export function ExecutionControls({
  status,
  onPause,
  onResume,
  onCancel,
  onRetry,
  loading,
}: ExecutionControlsProps) {
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isFinished =
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled' ||
    status === 'timed_out';

  return (
    <div className="flex items-center gap-2">
      {isRunning && onPause && (
        <Button
          variant="secondary"
          size="sm"
          icon={<Pause className="h-4 w-4" />}
          onClick={onPause}
          loading={loading}
        >
          Pause
        </Button>
      )}

      {isPaused && onResume && (
        <Button
          variant="primary"
          size="sm"
          icon={<Play className="h-4 w-4" />}
          onClick={onResume}
          loading={loading}
        >
          Resume
        </Button>
      )}

      {(isRunning || isPaused || status === 'waiting_approval') && onCancel && (
        <Button
          variant="danger"
          size="sm"
          icon={<XCircle className="h-4 w-4" />}
          onClick={onCancel}
          loading={loading}
        >
          Cancel
        </Button>
      )}

      {isFinished && onRetry && (
        <Button
          variant="secondary"
          size="sm"
          icon={<RotateCcw className="h-4 w-4" />}
          onClick={onRetry}
          loading={loading}
        >
          Retry
        </Button>
      )}
    </div>
  );
}
